import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { IconButton, Tooltip } from '@mui/material';
import { CopyIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { copyToClipboard } from 'utils/copyToClipboard';

import { showMessageAction } from 'store/global/actions';

const ButtonWithTooltip = ({
  iconButtonClassName = '',
  iconClassName = '',
  spanContainerClassName = '',
  placement = 'bottom',
  icon: IconComponent,
  iconSize,
  iconDirection,
  iconOrientation,
  title,
  onClick = _.noop,
  PopperProps,
  disablePortal = false,
  withCopiedValue = '',
  dataTestId,
  ...props
}) => {
  const dispatch = useDispatch();

  const poppperProps = useMemo(
    () => ({ disablePortal, ...PopperProps }),
    [PopperProps, disablePortal],
  );

  const onCopyTooltip = useCallback(
    e => {
      e.stopPropagation();
      copyToClipboard(withCopiedValue);
      dispatch(showMessageAction({ message: `${title} copied to clipboard`, variant: 'success' }));
    },
    [withCopiedValue, title, dispatch],
  );

  const tooltipTitle = useMemo(() => {
    if (!withCopiedValue) {
      return title;
    }

    return (
      <span className="inline-flex items-center" onClick={onCopyTooltip}>
        <span className="pr-4">{title}</span>
        <CopyIcon size={14} />
      </span>
    );
  }, [title, withCopiedValue, onCopyTooltip]);

  return (
    <Tooltip PopperProps={poppperProps} placement={placement} title={tooltipTitle}>
      <span className={spanContainerClassName}>
        <IconButton
          className={clsx('p-8', iconButtonClassName)}
          data-testid={dataTestId}
          onClick={onClick}
          {...props}
        >
          <IconComponent
            className={iconClassName}
            direction={iconDirection}
            orientation={iconOrientation}
            size={iconSize}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

ButtonWithTooltip.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  icon: PropTypes.elementType.isRequired,
  iconSize: PropTypes.number,
  iconDirection: PropTypes.string,
  iconOrientation: PropTypes.string,
  PopperProps: PropTypes.object,
  disablePortal: PropTypes.bool,
  withCopiedValue: PropTypes.string,
  placement: PropTypes.string,
  iconButtonClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  spanContainerClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  onClick: PropTypes.func,
};

export default memo(ButtonWithTooltip);
